"use client";

import Link from "next/link";
import { Fragment } from "react";
import { useLocale } from "next-intl";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Breadcrumb navigation component
 *
 * Usage:
 * <Breadcrumb items={[
 *     { label: "Home", href: "/" },
 *     { label: "Templates", href: "/templates" },
 *     { label: "Current Page" }
 * ]} />
 */
export function Breadcrumb({ items, className = "" }: BreadcrumbProps) {
  const locale = useLocale();

  // A bare "/" means "home". It has to stay locale-aware: hardcoding it sent
  // Spanish visitors (and their link equity) to the English homepage. Only the
  // exact root is rewritten, so already-localized hrefs are left untouched.
  const localizeHome = (href?: string) =>
    href === "/" && locale !== "en" ? `/${locale}` : href;

  return (
    <nav className={`mb-6 ${className}`} aria-label="Breadcrumb">
      <ol className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 flex-wrap">
        {items.map((item, index) => (
          <Fragment key={index}>
            {index > 0 && (
              <li className="text-gray-400 dark:text-gray-500">&gt;</li>
            )}
            <li
              className={
                index === items.length - 1
                  ? "text-gray-900 dark:text-white font-medium"
                  : ""
              }
            >
              {item.href ? (
                <Link
                  href={localizeHome(item.href) ?? "/"}
                  className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                item.label
              )}
            </li>
          </Fragment>
        ))}
      </ol>
    </nav>
  );
}
